import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  GameController, BookOpen, GraduationCap, Megaphone, ChartBar, 
  Lightning, Trophy, ArrowRight, TrendUp
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const KPI = ({ icon: Icon, label, value, accent = '#F59E0B', sub }) => (
  <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4 hover:bg-white/[0.04] transition-colors" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center gap-2 mb-3">
      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${accent}12` }}>
        <Icon size={16} weight="duotone" style={{ color: accent }} />
      </div>
      <span className="text-[11px] text-[#666] uppercase tracking-wider font-medium">{label}</span>
    </div>
    <p className="text-2xl font-bold text-white tracking-tight">{value}</p>
    {sub && <p className="text-[11px] text-[#555] mt-1">{sub}</p>}
  </div>
);

export default function PublisherDashboard() {
  const { publisherId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/api/publisher-portal/${publisherId}/dashboard`, { withCredentials: true })
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [publisherId]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-10 w-56 bg-white/5 rounded animate-pulse" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => <div key={i} className="h-28 bg-white/5 rounded-xl animate-pulse" />)}
        </div>
      </div>
    );
  }

  if (!data) return <p className="text-[#666]">Failed to load dashboard.</p>;

  const base = `/publisher/${publisherId}`;

  return (
    <div data-testid="publisher-dashboard">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight" style={{ fontFamily: 'Unbounded' }}>
            {data.publisher_name}
          </h1>
          <p className="text-sm text-[#666] mt-1">Publisher Dashboard</p>
        </div>
        {data.verified && (
          <span className="text-[11px] px-3 py-1.5 rounded-full bg-[#10B981]/10 text-[#10B981] font-medium border border-[#10B981]/20">
            Verified Publisher
          </span>
        )}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <KPI icon={GameController} label="Games" value={data.total_games} accent="#9D4CDD" />
        <KPI icon={BookOpen} label="Rulesets" value={data.total_rulesets} accent="#00F0FF" sub={`${data.total_rulesets} published`} />
        <KPI icon={GraduationCap} label="Tutorials" value={data.total_tutorials} accent="#10B981" />
        <KPI icon={Megaphone} label="Campaigns" value={data.total_campaigns} accent="#F59E0B" sub={`${data.active_campaigns} active`} />
        <KPI icon={Lightning} label="Editions" value={data.total_editions} accent="#EC4899" />
        <KPI icon={Lightning} label="Expansions" value={data.total_expansions} accent="#3B82F6" />
        <KPI icon={ChartBar} label="Sessions" value={data.total_sessions} accent="#00F0FF" sub="Players using your games" />
        <KPI icon={Trophy} label="Tournaments" value={data.total_tournaments} accent="#F59E0B" />
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <button onClick={() => navigate(`${base}/rules`)}
          className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5 text-left hover:bg-white/[0.04] transition-colors group"
          data-testid="quick-rules">
          <BookOpen size={24} weight="duotone" className="text-[#00F0FF] mb-3" />
          <p className="text-white font-semibold">Manage Rules</p>
          <p className="text-xs text-[#666] mt-1">Upload official rules and quick reference sheets</p>
          <ArrowRight size={16} className="text-[#555] mt-3 group-hover:text-[#00F0FF] transition-colors" />
        </button>
        <button onClick={() => navigate(`${base}/tutorials`)}
          className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5 text-left hover:bg-white/[0.04] transition-colors group"
          data-testid="quick-tutorials">
          <GraduationCap size={24} weight="duotone" className="text-[#10B981] mb-3" />
          <p className="text-white font-semibold">Create Tutorial</p>
          <p className="text-xs text-[#666] mt-1">Build learn-to-play and setup guides</p>
          <ArrowRight size={16} className="text-[#555] mt-3 group-hover:text-[#10B981] transition-colors" />
        </button>
        <button onClick={() => navigate(`${base}/campaigns`)}
          className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5 text-left hover:bg-white/[0.04] transition-colors group"
          data-testid="quick-campaigns">
          <Megaphone size={24} weight="duotone" className="text-[#F59E0B] mb-3" />
          <p className="text-white font-semibold">Launch Campaign</p>
          <p className="text-xs text-[#666] mt-1">Promote games and drive learn-to-play events</p>
          <ArrowRight size={16} className="text-[#555] mt-3 group-hover:text-[#F59E0B] transition-colors" />
        </button>
      </div>
    </div>
  );
}
